import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../services/apiClient';
import { useUserStore } from '../stores/user';
export interface ProfileResType {
	error: boolean;
	response: ProfileDataType;
	success: boolean;
}

export interface ProfileDataType {
	User: UserType;
	UserProperty: UserPropertyType;
	VerifiedEmail: VerifiedEmailType;
}

export interface UserType {
	birthday: null;
	email: string;
	firstname: string;
	id: string;
	image: null;
	lastname: string;
	name: string;
}

export interface UserPropertyType {
	dni?: DniType;
	personal_data?: {
		dni?: DniType;
	};
}

export interface DniType {
	childs: any[];
	id: string;
	params: DniParamsType;
	value: string;
}

export interface DniParamsType {
	// El backend lo serializa como string ("1"/"2") en /profile.json aunque
	// la columna interna sea int. Aceptamos ambos formatos.
	type_id?: number | string;
}

export interface VerifiedEmailType {
	created: null;
	created_gmt: null;
	deleted: null;
	deleted_date: null;
	id: null;
	modified: null;
	modified_gmt: null;
	parent_id: null;
	property_key: null;
	property_params: null;
	property_type: null;
	property_value: null;
	user_id: null;
}

const useProfile = () => {
	const setUserData = useUserStore((state) => state.setUserData);

	const query = useQuery({
		queryKey: ['profile'],
		queryFn: async () => {
			const res = await apiClient.get<ProfileResType>('/profile.json');
			return res;
		},
		cacheTime: 30 * 60 * 1000,
	});

	const userProperty = query.data?.data?.response?.UserProperty;

	// El `type_id` del DNI puede venir como string ("2") o number (2).
	// Lo normalizamos a string para que el user store y los consumers
	// (mapDniType en Quiz/hooks) lo procesen sin sorpresas. Si no existe
	// en ninguno de los dos paths, queda `undefined` — los validadores
	// caerán a su default sin afirmar nada falso.
	const dniTypeId =
		userProperty?.personal_data?.dni?.params?.type_id ??
		userProperty?.dni?.params?.type_id;
	const dniType = dniTypeId != null ? String(dniTypeId) : undefined;

	const user = React.useMemo(
		() => ({
			...query.data?.data?.response?.User,
			dni: userProperty?.personal_data?.dni?.value ?? userProperty?.dni?.value,
			dni_type: dniType,
		}),
		[
			query.data?.data?.response?.User,
			userProperty?.personal_data?.dni?.value,
			userProperty?.dni?.value,
			dniType,
		]
	);

	React.useEffect(() => {
		setUserData(user);
	}, [user, setUserData]);

	return { ...query, user };
};

export default useProfile;
